import { Callout, DataTable, LockBadge, Panel } from "../../../../ds";
import type { Column } from "../../../../ds";

import { ExternalLinkButton } from "../../../locks/components/ExternalLinkButton";
import { IdentityUnavailableState } from "../../../locks/components/LockStates";
import { Link } from "react-router-dom";
import type { LocksState } from "./SpaceSettingsPage";
import { PageLockDTO } from "../../../locks/models/LockDTO";
import { formatLockDate } from "../../../locks/utils/lockDates";
import { identityWebIamUrl } from "../../../locks/utils/identityWeb";

const pageName = (lock: PageLockDTO) => lock.title || `#${lock.pageId}`;

function columns(spaceId: string): Column<PageLockDTO>[] {
    return [
        {
            key: "page",
            header: "Page",
            render: (lock) => (
                <span className="ark-settings-lock-page">
                    <LockBadge state="own" reason="Locked page" />
                    <Link to={`/spaces/${spaceId}/pages/${lock.pageId}`}>{pageName(lock)}</Link>
                </span>
            ),
        },
        { key: "reason", header: "Reason", render: (lock) => lock.reason || <span className="ark-muted">–</span> },
        { key: "locked-on", header: "Locked on", width: "12rem", render: (lock) => <span className="ark-settings-nowrap">{formatLockDate(lock.createdAt)}</span> },
        {
            key: "readers",
            header: "Readers",
            width: "11rem",
            align: "end",
            render: (lock) => {
                const manageUrl = lock.manageUrl || identityWebIamUrl(lock.scope);
                return manageUrl ? (
                    <span className="ark-settings-nowrap">
                        <ExternalLinkButton href={manageUrl} text="Manage readers" type="link" aria-label={`Manage readers of ${pageName(lock)}`} />
                    </span>
                ) : null;
            },
        },
    ];
}

/**
 * Locked pages of the space (`GET spaces/{id}/locks`, only the locks the caller reads): page with the one lock glyph, reason, date and
 * the lock's Identity Web page. Lock and unlock happen on the page itself.
 */
export function LockedPagesSection(props: { spaceId: string; locks: LocksState; onRetry: () => void }) {
    const { spaceId, locks } = props;
    const cols = columns(spaceId);
    const count = locks.status === "ready" ? locks.locks.length : undefined;

    let body;
    if (locks.status === "identity-unavailable") {
        body = <IdentityUnavailableState compact onRetry={props.onRetry} />;
    } else if (locks.status === "error") {
        body = <Callout tone="danger">{locks.message}</Callout>;
    } else {
        body = (
            <DataTable
                columns={cols}
                items={locks.status === "ready" ? locks.locks : undefined}
                isLoading={locks.status === "loading"}
                rowKey={(lock) => String(lock.pageId)}
                caption="Locked pages"
                emptyTitle="No locked pages"
                emptyText="No locked pages that you can read in this space."
                mobileCard={(lock) => (
                    <div className="ark-settings-lock-card">
                        {cols[0].render(lock)}
                        {lock.reason ? <span>{lock.reason}</span> : null}
                        <span className="ark-muted">Locked on {formatLockDate(lock.createdAt)}</span>
                        {cols[3].render(lock)}
                    </div>
                )}
            />
        );
    }

    return (
        <Panel title="Locked pages" count={count}>
            <div className="ark-stack">
                <p className="ark-settings-sub">
                    Locking hides a page and its children from everyone except the lock's readers, space Owners and platform admins. Lock
                    and unlock pages from the page itself. Only the locks you can read are listed.
                </p>
                {body}
            </div>
        </Panel>
    );
}
